import React, { useState, useEffect } from 'react';
import { FaUser, FaPhone, FaEnvelope, FaSearch, FaShoppingBag, FaDollarSign, FaMapMarkerAlt } from 'react-icons/fa';
import { customerService } from '../../services/customerService';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = customers.filter(customer => 
        (customer.name && customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.phone && customer.phone.includes(searchTerm)) ||
        (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, customers]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await customerService.getCustomers();
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-6 text-white">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Customer Management</h1>
          <p className="text-primary-100">View and manage your customer base</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-4">
        <div className="relative">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-900"
          />
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaUser className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Total Customers</p>
              <p className="text-2xl font-bold">{customers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaShoppingBag className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Total Purchases</p>
              <p className="text-2xl font-bold">{customers.reduce((acc, c) => acc + (c.totalPurchases || 0), 0)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaDollarSign className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Total Revenue</p>
              <p className="text-lg font-bold">RS:{customers.reduce((acc, c) => acc + (c.totalSpent || 0), 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Grid */}
      {filteredCustomers.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-12 text-center">
          <div className="text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">No customers found</h3>
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm ? 'Try a different search term' : 'No customers registered yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredCustomers.map((customer, index) => (
              <motion.div
                key={customer._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft hover:shadow-hard transition-all duration-300 overflow-hidden"
              >
                {/* Customer Header */}
                <div className="relative h-24 bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                  <div className="absolute -bottom-8 left-6">
                    <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-lg border-4 border-white dark:border-gray-800">
                      <FaUser className="text-2xl text-primary-600" />
                    </div>
                  </div>
                </div>

                {/* Customer Content */}
                <div className="pt-10 px-6 pb-6">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-white mb-1">
                    {customer.name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    ID: {customer._id.slice(-6)}
                  </p>

                  <div className="space-y-3">
                    {customer.phone && (
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <FaPhone className="text-blue-600 dark:text-blue-400 text-xs" />
                      </div>
                      <span className="font-medium">{customer.phone || 'N/A'}</span>
                    </div>
                    )}
                    {customer.email && (
                      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                          <FaEnvelope className="text-green-600 dark:text-green-400 text-xs" />
                        </div>
                        <span className="font-medium">{customer.email}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                        <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                          <FaMapMarkerAlt className="text-purple-600 dark:text-purple-400 text-xs" />
                        </div>
                        <span className="font-medium">{customer.address}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Purchases</p>
                      <p className="font-bold text-lg text-gray-800 dark:text-white">{customer.totalPurchases || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Total Spent</p>
                      <p className="font-bold text-lg text-gray-800 dark:text-white">RS:{(customer.totalSpent || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
